import { UpdateData, DocumentData } from 'firebase/firestore';
import { Task, FullProject, StakeholderEntry } from '../../../types';
import {
  isCommProject,
  areProjectIdsMatching,
  isMatchingTaskId,
} from '../../../utils/idNormalizer';
import { normalizePersonName } from '../../../components/communication/personModel';

export interface ParsedPoolContactId {
  cleanPoolId: string;
  poolContactName: string;
  poolTargetProjectId: string;
}

export function parsePoolContactDeleteId(
  taskIdStr: string,
  allProjects: FullProject[],
  currentProjectId: string
): ParsedPoolContactId | null {
  if (!taskIdStr || !taskIdStr.includes('pool_contact_')) return null;

  const poolIdx = taskIdStr.indexOf('pool_contact_');
  const cleanPoolId = taskIdStr.substring(poolIdx);
  let poolContactName = '';
  let poolTargetProjectId = '';

  const matchedProj = allProjects.find((p) => cleanPoolId.startsWith(`pool_contact_${p.id}_`));
  if (matchedProj) {
    poolTargetProjectId = matchedProj.id;
    const encoded = cleanPoolId.substring(`pool_contact_${matchedProj.id}_`.length);
    try {
      poolContactName = decodeURIComponent(encoded);
    } catch {
      poolContactName = encoded;
    }
  } else {
    const prefix = `pool_contact_${currentProjectId}_`;
    if (cleanPoolId.startsWith(prefix)) {
      poolTargetProjectId = currentProjectId;
      const encoded = cleanPoolId.substring(prefix.length);
      try {
        poolContactName = decodeURIComponent(encoded);
      } catch {
        poolContactName = encoded;
      }
    } else {
      const withoutPrefix = cleanPoolId.replace('pool_contact_', '');
      const underscoreIdx = withoutPrefix.indexOf('_');
      if (underscoreIdx !== -1) {
        poolTargetProjectId = withoutPrefix.substring(0, underscoreIdx);
        const encoded = withoutPrefix.substring(underscoreIdx + 1);
        try {
          poolContactName = decodeURIComponent(encoded);
        } catch {
          poolContactName = encoded;
        }
      } else {
        try {
          poolContactName = decodeURIComponent(withoutPrefix);
        } catch {
          poolContactName = withoutPrefix;
        }
      }
    }
  }

  if (!poolContactName) return null;

  return {
    cleanPoolId,
    poolContactName,
    poolTargetProjectId,
  };
}

export interface UnassignedTaskBackup {
  projectId: string;
  taskId: string | number;
  previousAssignee: string;
  previousContactId?: string | number;
  previousPersonKey?: string;
}

export interface RemovedStakeholderBackup {
  projectId: string;
  stakeholder: StakeholderEntry;
}

export interface PoolContactDeletionPlan {
  poolContactName: string;
  isGlobalScope: boolean;
  targetPId: string;
  removedTaskIds: Set<string>;
  removedPersonKeys: Set<string>;
  unassignedTaskIds: Set<string>;
  modifiedProjectIds: Set<string>;
  removedTasksList: Task[];
  unassignedTasksToPersist: Array<{ id: string; updates: UpdateData<DocumentData> }>;
  unassignedTasksBackup: UnassignedTaskBackup[];
  removedStakeholdersBackup: RemovedStakeholderBackup[];
  updatedProjects: FullProject[];
  cleanedRawTasks: Task[];
  cleanedOwnedTasks: Task[];
}

export function planPoolContactDeletion(params: {
  taskIdStr: string;
  poolContactName: string;
  poolTargetProjectId: string;
  currentProjectId: string;
  COMM_PROJECT_ID: string;
  allProjects: FullProject[];
  rawTasks: Task[];
  ownedTasks: Task[];
}): PoolContactDeletionPlan {
  const {
    taskIdStr,
    poolContactName,
    poolTargetProjectId,
    currentProjectId,
    COMM_PROJECT_ID,
    allProjects,
    rawTasks,
    ownedTasks,
  } = params;

  const isGlobalScope =
    !poolTargetProjectId ||
    poolTargetProjectId === 'global' ||
    poolTargetProjectId === 'all' ||
    poolTargetProjectId.includes('comm_internal') ||
    !currentProjectId ||
    currentProjectId === 'global' ||
    currentProjectId.includes('comm_internal');

  const targetPId = poolTargetProjectId || currentProjectId;
  const lowerContactName = poolContactName.trim().toLowerCase();
  const normContactName = normalizePersonName(poolContactName);

  const matchesContactName = (val?: string | null) => {
    if (!val) return false;
    const trimmed = val.trim().toLowerCase();
    if (trimmed === lowerContactName) return true;
    return Boolean(normContactName && normalizePersonName(val) === normContactName);
  };

  const removedTaskIds = new Set<string>();
  const removedPersonKeys = new Set<string>();
  if (normContactName) removedPersonKeys.add(`n:${normContactName}`);

  const unassignedTaskIds = new Set<string>();
  const modifiedProjectIds = new Set<string>();
  const removedTasksList: Task[] = [];
  const unassignedTasksToPersist: Array<{ id: string; updates: UpdateData<DocumentData> }> = [];
  const unassignedTasksBackup: UnassignedTaskBackup[] = [];
  const removedStakeholdersBackup: RemovedStakeholderBackup[] = [];

  const isPersonContactTask = (t: Task) =>
    (t.type === 'contact' || t.status === 'pool') &&
    (matchesContactName(t.name) || matchesContactName(t.assignee));

  allProjects.forEach((p) => {
    const isTarget =
      isGlobalScope || areProjectIdsMatching(p.id, targetPId) || isCommProject(p.id);
    if (!isTarget) return;
    (p.tasks || []).forEach((t) => {
      if (isPersonContactTask(t)) {
        removedTaskIds.add(String(t.id));
        if (t.contactEmail?.trim()) {
          removedPersonKeys.add(`e:${t.contactEmail.trim().toLowerCase()}`);
        }
        removedTasksList.push({ ...t, projectId: p.id });
      }
    });
  });

  [...rawTasks, ...ownedTasks].forEach((t) => {
    if (isPersonContactTask(t)) {
      const tProjId = t.projectId || COMM_PROJECT_ID;
      const isTarget =
        isGlobalScope || areProjectIdsMatching(tProjId, targetPId) || isCommProject(tProjId);
      if (isTarget) {
        removedTaskIds.add(String(t.id));
        if (t.contactEmail?.trim()) {
          removedPersonKeys.add(`e:${t.contactEmail.trim().toLowerCase()}`);
        }
      }
    }
  });

  allProjects.forEach((p) => {
    const isTarget =
      isGlobalScope || areProjectIdsMatching(p.id, targetPId) || isCommProject(p.id);
    if (!isTarget) return;
    (p.tasks || []).forEach((t) => {
      if (removedTaskIds.has(String(t.id)) || isPersonContactTask(t)) return;
      const assignees = (t.assignee || '')
        .split(/[,;/]/)
        .map((a) => a.trim())
        .filter(Boolean);
      const hasAssignee = assignees.some((a) => matchesContactName(a));
      const hasContactId = Boolean(
        t.contactId !== undefined && t.contactId !== '' && removedTaskIds.has(String(t.contactId))
      );
      const hasPersonKey = Boolean(t.personKey && removedPersonKeys.has(t.personKey));
      if (hasAssignee || hasContactId || hasPersonKey) {
        unassignedTasksBackup.push({
          projectId: p.id,
          taskId: t.id,
          previousAssignee: t.assignee || '',
          previousContactId: t.contactId,
          previousPersonKey: t.personKey,
        });
      }
    });
    (p.metadata?.stakeholders || []).forEach((s: StakeholderEntry) => {
      if (matchesContactName(s?.name)) {
        removedStakeholdersBackup.push({ projectId: p.id, stakeholder: s });
      }
    });
  });

  const cleanTaskReference = (
    t: Task
  ): { task: Task; changed: boolean; updates: UpdateData<DocumentData> } => {
    let changed = false;
    const updates: UpdateData<DocumentData> = {};
    let nextTask = t;

    if (t.assignee) {
      const assignees = t.assignee
        .split(/[,;/]/)
        .map((a) => a.trim())
        .filter(Boolean);
      if (assignees.some((a) => matchesContactName(a))) {
        const remaining = assignees.filter((a) => !matchesContactName(a)).join(', ');
        nextTask = { ...nextTask, assignee: remaining };
        updates.assignee = remaining;
        changed = true;
      }
    }
    if (
      t.contactId !== undefined &&
      t.contactId !== '' &&
      removedTaskIds.has(String(t.contactId))
    ) {
      nextTask = { ...nextTask, contactId: '' };
      updates.contactId = '';
      changed = true;
    }
    if (t.personKey && removedPersonKeys.has(t.personKey)) {
      nextTask = { ...nextTask, personKey: '' };
      updates.personKey = '';
      changed = true;
    }
    if (changed) {
      const now = Date.now();
      nextTask = { ...nextTask, updatedAt: now };
      updates.updatedAt = now;
    }
    return { task: nextTask, changed, updates };
  };

  const updatedProjects = allProjects.map((p) => {
    const isTarget =
      isGlobalScope || areProjectIdsMatching(p.id, targetPId) || isCommProject(p.id);
    if (!isTarget) return p;

    let projChanged = false;
    const newTasks = (p.tasks || [])
      .filter((t) => {
        if (removedTaskIds.has(String(t.id)) || isPersonContactTask(t)) {
          removedTaskIds.add(String(t.id));
          projChanged = true;
          return false;
        }
        return true;
      })
      .map((t) => {
        const { task: cleaned, changed, updates } = cleanTaskReference(t);
        if (changed) {
          const idStr = String(t.id);
          if (!unassignedTaskIds.has(idStr)) {
            unassignedTaskIds.add(idStr);
            unassignedTasksToPersist.push({ id: idStr, updates });
          }
          projChanged = true;
        }
        return cleaned;
      });

    let newMetadata = p.metadata;
    if (p.metadata?.stakeholders) {
      const initialLen = p.metadata.stakeholders.length;
      const filteredStakeholders = p.metadata.stakeholders.filter(
        (s: StakeholderEntry) => !matchesContactName(s?.name)
      );
      if (filteredStakeholders.length !== initialLen) {
        projChanged = true;
        newMetadata = { ...newMetadata, stakeholders: filteredStakeholders };
      }
    }

    if (projChanged) {
      modifiedProjectIds.add(p.id);
      if (p.rawId) modifiedProjectIds.add(p.rawId);
      return { ...p, tasks: newTasks, metadata: newMetadata, lastModified: Date.now() };
    }
    return p;
  });

  const filterAndCleanTaskList = (list: Task[]) =>
    list
      .filter((t) => {
        if (isMatchingTaskId(t.id, taskIdStr)) return false;
        if (removedTaskIds.has(String(t.id))) return false;
        if (isPersonContactTask(t)) return false;
        return true;
      })
      .map((t) => cleanTaskReference(t).task);

  const cleanedRawTasks = filterAndCleanTaskList(rawTasks);
  const cleanedOwnedTasks = filterAndCleanTaskList(ownedTasks);

  return {
    poolContactName,
    isGlobalScope,
    targetPId,
    removedTaskIds,
    removedPersonKeys,
    unassignedTaskIds,
    modifiedProjectIds,
    removedTasksList,
    unassignedTasksToPersist,
    unassignedTasksBackup,
    removedStakeholdersBackup,
    updatedProjects,
    cleanedRawTasks,
    cleanedOwnedTasks,
  };
}
